import React, { useState, useEffect } from "react";
import Layouts from "../../components/Layouts";
import {
  Button,
  Table,
  Space,
  Tag,
  message,
  Modal,
  Empty,
  Pagination,
  Input,
} from "antd";
import { Link } from "react-router-dom";
import { getDirectusClient } from "../../lib/directus";
import {
  readItems,
  updateItem,
  readUser,
  updateUser,
} from "@directus/sdk";
import { useSelector } from "react-redux";
import validarpermiso from "../../lib/utils";
import LoanDetailsModal from "../loans_routes/components/LoanDetailsModal";
import { DownloadOutlined } from "@ant-design/icons";
import { exportLoansExcel } from "../loans_routes/exportLoansExcel";


const { Search } = Input;

function TroopLoan() {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasPermissionError, setHasPermissionError] = useState(false);
  const permisos = useSelector((state) => state.permisos);
  const userRoleID = useSelector((state) => state.userdata.role?.name);
  const userBodega = useSelector((state) => state.userdata.bodega?.id);
  const user = useSelector((state) => state.userdata.id);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [detailsLoan, setDetailsLoan] = useState(null);
  const [serialesCounts, setSerialesCounts] = useState({});
  const [loadingCounts, setLoadingCounts] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  const getStatusColor = (status) => {
    const colors = {
      activo: "green",
      anulado: "red",
      cerrado: "gray",
      devolucion: "blue",
    };
    return colors[status] || "gold";
  };

  const fetchSerialesCounts = async (prestamos) => {
    setLoadingCounts(true);
    const directus = await getDirectusClient();
    const counts = {};
    
    // Crear un array de promesas para hacer todas las consultas en paralelo
    const countPromises = prestamos.map(async (prestamo) => {
      try {
        const countResponse = await directus.request(
          readItems("seriales", {
            filter: {
              prestamo: {
                prestamos_id: { _eq: prestamo.id },
              },
            },
            aggregate: { countDistinct: "id" },
          })
        );
        return {
          prestamoId: prestamo.id,
          count: countResponse[0]?.countDistinct?.id || 0
        };
      } catch (error) {
        console.error(`Error fetching count for prestamo ${prestamo.id}:`, error);
        return {
          prestamoId: prestamo.id,
          count: 0
        };
      }
    });

    // Esperar todas las promesas y construir el objeto counts
    const results = await Promise.all(countPromises);
    results.forEach(({ prestamoId, count }) => {
      counts[prestamoId] = count;
    });
    
    setSerialesCounts(counts);
    setLoadingCounts(false);
  };

  const fetchLoans = async (page = 1, pageSize = 10) => {
    if (!validarpermiso("prestamos", "read", permisos)) {
      setHasPermissionError(true);
      return;
    }

    setLoading(true);
    try {
      const directus = await getDirectusClient();

      // Build filter object
      const filterObj = {
        _and: [
          {
            vendedor: {
             
              role:{name:{_eq:"supervisor tropas"}} 
            },
            ...(userRoleID !== "adminapp" && userRoleID !== "Administrator"
              ? {
                  vendedor: {
                    bodega: {
                      id: { _eq: userBodega },
                    },
                    role:{name:{_eq:"supervisor tropas"}} 
                  },
                }
              : {}),
          },
        ],
      };

      // Add search filter if search text exists
      if (searchText) {
        filterObj._and.push({
          vendedor: {
            _or: [{ first_name: { _contains: searchText } }],
          },
        });
      }

      // Get total count with filters
      const countResponse = await directus.request(
        readItems("prestamos", {
          aggregate: { count: "id" },
          filter: filterObj,
        })
      );

      const totalItems = countResponse[0]?.count?.id;

      // Get filtered and paginated data
      const response = await directus.request(
        readItems("prestamos", {
          fields: [
            "*",
            "vendedor.id",
            "vendedor.first_name",
            "vendedor.role.name",
            "vendedor.rol.last_name",
          ],
          filter: filterObj,
          sort: ["-date_created"],
          limit: pageSize,
          offset: (page - 1) * pageSize,
        })
      );

      setLoans(response);
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
        total: totalItems,
      });
      setHasPermissionError(false);
      
      // Limpiar conteos anteriores antes de cargar los nuevos
      setSerialesCounts({});
      
      // Cargar los conteos de seriales
      if (response.length > 0) {
        fetchSerialesCounts(response);
      }
    } catch (error) {
      console.error("Error fetching loans:", error);
      if (error.response?.status === 403) {
        setHasPermissionError(true);
        message.error("No tiene permisos para ver préstamos");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoans(pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize]);

  

  const showLoanDetails = (loan) => {
    setDetailsLoan(loan);
    setDetailsModalVisible(true);
  };

  const handleCancelLoan = async (loan) => {
    if (!validarpermiso("prestamos", "update", permisos)) {
      message.error("No tiene permisos para anular préstamos");
      return;
    }

    if (!user) {
      message.error("No se pudo identificar al usuario que anula el préstamo");
      return;
    }

    Modal.confirm({
      title: "¿Está seguro de anular este préstamo?",
      content: "Esta acción cambiará el estado del préstamo a anulado y devolverá todos los productos prestados.",
      okText: "Sí, anular",
      cancelText: "No",
      onOk: async () => {
        try {
          // Mostrar modal de progreso
          const progressModal = Modal.info({
            title: "Calculando total del préstamo",
            content: "Calculando el total a devolver...",
            okButtonProps: { style: { display: "none" } },
            closable: false,
          });

          // Calcular el total del préstamo
          const directus = await getDirectusClient();
          let totalToReturn = 0;

          // Obtener todos los seriales del préstamo
          const allSerials = await directus.request(
            readItems("seriales", {
              fields: ["*", "producto.*", "producto.grupo.*"],
              filter: {
                prestamo: {
                  prestamos_id: { _eq: loan.id },
                },
              },
              limit: -1,
            })
          );

          // Obtener todos los productos no serializados del préstamo
          const allNonSerialized = await directus.request(
            readItems("inventario_ns_prestamo", {
              fields: ["*", "inventario_ns.*", "inventario_ns.producto.*", "inventario_ns.producto.grupo.*"],
              filter: {
                prestamos: {
                  prestamos_id: { _eq: loan.id },
                },
              },
              limit: -1,
            })
          );

          // Calcular total de seriales
          if (allSerials && allSerials.length > 0) {
            totalToReturn = allSerials.reduce((total, item) => {
              const precio = parseFloat(item.producto?.preciosinimp) || 0;
              const impuesto = parseFloat(item.producto?.grupo?.impuesto) || 0;
              const precioConImpuesto = precio + (precio * impuesto) / 100;
              return total + precioConImpuesto;
            }, 0);
          }

          // Calcular total de productos no serializados
          if (allNonSerialized && allNonSerialized.length > 0) {
            const nonSerializedTotal = allNonSerialized.reduce((total, item) => {
              const cantidad = parseFloat(item.cantidad) || 0;
              const precio = parseFloat(item.inventario_ns?.producto?.preciosinimp) || 0;
              const impuesto = parseFloat(item.inventario_ns?.producto?.grupo?.impuesto) || 0;
              const precioConImpuesto = precio + (precio * impuesto) / 100;
              return total + cantidad * precioConImpuesto;
            }, 0);
            totalToReturn += nonSerializedTotal;
          }

          console.log("Total calculado a devolver:", totalToReturn);

          // Actualizar modal de progreso
          progressModal.update({
            title: "Anulando préstamo",
            content: "Procesando anulación del préstamo...",
          });

          // Llamar al endpoint de la API para anular el préstamo
          const baseUrl = process.env.REACT_APP_URL_COMPLEMENTO || '';
          const response = await fetch(`${baseUrl}/api/prestamos/anular`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              prestamoId: loan.id,
              usuarioAnuladorId: user,
              total: totalToReturn
            }),
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Error al anular préstamo: ${response.status} - ${errorText}`);
          }

          const result = await response.json();
          console.log('Respuesta de anulación:', result);

          // Cerrar modal de progreso
          progressModal.destroy();

          message.success(`Préstamo anulado exitosamente. Total devuelto: $${totalToReturn.toLocaleString()}`);

          // Recargar la lista de préstamos
          fetchLoans(pagination.current, pagination.pageSize);
        } catch (error) {
          console.error("Error canceling loan:", error);
          message.error("Error al anular el préstamo: " + (error.message || "Error desconocido"));
        }
      },
    });
  };

  const handleSearch = async (value) => {
    try {
      setLoading(true);
      const directus = await getDirectusClient();

      // Build filter object with the value directly
      const filterObj = {
        _and: [
          {
            vendedor: {
              bodega: {
                id: { _eq: userBodega },
              },
            },
          },
        ],
      };

      // Add search filter using the value parameter
      if (value) {
        filterObj._and.push({
          vendedor: {
            _or: [
              { first_name: { _contains: value } },
              { last_name: { _contains: value } },
            ],
          },
        });
      }

      // Get total count with filters
      const countResponse = await directus.request(
        readItems("prestamos", {
          aggregate: { count: "id" },
          filter: filterObj,
        })
      );

      const totalItems = countResponse[0]?.count?.id;

      // Get filtered data
      const response = await directus.request(
        readItems("prestamos", {
          fields: ["*", "vendedor.*", "seriales.*", "seriales.producto.*"],
          filter: filterObj,
          sort: ["-date_created"],
          limit: pagination.pageSize,
          offset: 0,
        })
      );

      setSearchText(value);
      setLoans(response);
      setPagination({
        ...pagination,
        current: 1,
        total: totalItems,
      });
      
      // Limpiar conteos anteriores y cargar los nuevos
      setSerialesCounts({});
      if (response.length > 0) {
        fetchSerialesCounts(response);
      }
    } catch (error) {
      console.error("Error searching loans:", error);
      message.error("Error al buscar préstamos");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      await exportLoansExcel({
        getDirectusClient,
        readItems,
        userRoleID, 
        userBodega,
        permisos,
        validarpermiso,
        tipo:"supervisor tropas"
      });
    } catch (error) {
      console.error("Error downloading Excel:", error);
    } finally {
      setDownloadingExcel(false);
    }
  };

  const columns = [
    {
      title: "Vendedor",
      dataIndex: ["vendedor", "first_name"],
      key: "vendedor",
      render: (text, record) =>
        `${record.vendedor?.first_name || ""} ${
          record.vendedor?.last_name || ""
        }`,
    },
    {
      title: "Fecha",
      dataIndex: "date_created",
      key: "date",
      render: (text) => new Date(text).toLocaleDateString(),
    },
    {
      title: "Tipo",
      dataIndex: "tipo",
      key: "tipo",
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      render: (estado) => (
        <Tag color={getStatusColor(estado)}>{estado || "pendiente"}</Tag>
      ),
    },
    {
      title: "# Seriales",
      key: "seriales",
      render: (_, record) => {
        // Si tenemos el conteo en serialesCounts, mostrarlo
        if (serialesCounts[record.id] !== undefined) {
          return serialesCounts[record.id];
        }
        
        // Si no tenemos el conteo pero tenemos seriales en el record, mostrar el length
        if (record.seriales && record.seriales.length > 0) {
          return record.seriales.length;
        }
        
        // Si estamos cargando los conteos, mostrar un indicador de carga
        return loadingCounts ? "..." : 0;
      },
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_, record) => (
        <Space>
          {validarpermiso("prestamos", "read", permisos) && (
            <Button type="primary" onClick={() => showLoanDetails(record)}>
              Ver detalles
            </Button>
          )}
          {validarpermiso("prestamos", "update", permisos) &&
            (record.estado === "solicitud prestamo" ||
              record.estado === "solicitud_prestamo") && ( // Add this condition
              <Button
                type="primary"
                danger
                onClick={() => handleCancelLoan(record)}
              >
                Anular préstamo
              </Button>
            )}
        </Space>
      ),
    },
  ];
 

  return (
    <Layouts>
      {!hasPermissionError ? (
        <>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Space
              style={{
                marginBottom: 16,
                width: "100%",
                justifyContent: "space-between",
              }}
            >
            
            <Space style={{ marginBottom: 16 }}>
              {validarpermiso("prestamos", "create", permisos) && (
                <Link to="/troop-loans_create">
                  <Button type="primary">Crear préstamo</Button>
                </Link>
              )}
              {validarpermiso("prestamos", "update", permisos) && (
                <Link to="/troop-loans_repay">
                  <Button>Devolver préstamo</Button>
                </Link>
              )}
              {validarpermiso("prestamos", "read", permisos) && (
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadExcel}
                  loading={downloadingExcel}
                  disabled={downloadingExcel}
                >
                  {downloadingExcel ? "Descargando..." : "Descargar préstamos"}
                </Button>
              )}
            </Space>
              <Search
                placeholder="Buscar por supervisor"
                allowClear
                onSearch={handleSearch}
                style={{ width: 300 }}
                enterButton
              />
            </Space>

            <Table
              columns={columns}
              dataSource={loans}
              rowKey="id"
              loading={loading}
              pagination={false}
            />

            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              showSizeChanger={true}
              showQuickJumper={true}
              showTotal={(total) => `Total: ${total} préstamos`}
              pageSizeOptions={["10", "25", "50", "100"]}
              onChange={(page, pageSize) => {
                setPagination({
                  ...pagination,
                  current: page,
                  pageSize: pageSize,
                });
              }}
              style={{ marginTop: 16, textAlign: "right" }}
              disabled={!validarpermiso("prestamos", "read", permisos)}
            />
          </Space>
        </>
      ) : (
        <h3>No tiene permisos para ver préstamos</h3>
      )}
      <LoanDetailsModal
        visible={detailsModalVisible}
        onCancel={() => {
          setDetailsModalVisible(false);
          setDetailsLoan(null);
         
        }}
        selectedLoan={detailsLoan}
        serialesCounts={serialesCounts}
      />
    </Layouts>
  );
}

export default TroopLoan;
